/**
 * Filter Registry — Dynamic filter section configuration
 *
 * Defines filter sections with their required field patterns. Filter sections
 * only render when their required fields exist in the available field set.
 * Filter-to-column dependencies are auto-derived from field patterns,
 * replacing the previous manual `filterColumnDependencies` mapping.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FilterSectionConfig {
  /** Unique key matching the filter section (e.g., 'payments', 'applications') */
  key: string;
  /** Display label for the section header */
  label: string;
  /**
   * Field name patterns (regex) that this filter section depends on.
   * If ANY pattern matches at least one available field, the section is shown.
   */
  fieldPatterns: RegExp[];
  /**
   * Specific filter-to-column mappings for this section.
   * Keys are controller query param names, values are required column field keys.
   */
  filterDependencies: Record<string, string[]>;
  /** Sort order for display (lower = higher) */
  order: number;
  /** Whether this section requires "Show Individual Applications" to be enabled */
  requiresIndividualApplications?: boolean;
  /** Whether this section is admin-only */
  adminOnly?: boolean;
}

// ---------------------------------------------------------------------------
// Section Definitions
// ---------------------------------------------------------------------------

const FILTER_SECTIONS: FilterSectionConfig[] = [
  {
    key: 'partner',
    label: 'Partner',
    fieldPatterns: [/company/i],
    filterDependencies: {},
    order: 0,
    adminOnly: true,
  },
  {
    key: 'programs',
    label: 'Programs',
    fieldPatterns: [/program_instance|programInstance|program_template|programTemplate/i],
    filterDependencies: {
      programTemplateId: ['tas_program_template_field_program_name_keyword'],
      programCreatedAtStart: ['tas_program_instance_created_at_date'],
      programCreatedAtEnd: ['tas_program_instance_created_at_date'],
      excludeNonVoucherPrograms: ['employee_date_of_birth', 'student_id'],
      courseCashProgramsOnly: [],
    },
    order: 10,
  },
  {
    key: 'applications',
    label: 'Applications',
    fieldPatterns: [/tas_app|tasApp|application/i],
    filterDependencies: {
      showIndividualApplications: ['tas_application_id_keyword'],
      applicationStates: ['tas_application_state_keyword'],
      applicationCreatedAtStart: ['tas_application_created_at_date'],
      applicationCreatedAtEnd: ['tas_application_created_at_date'],
    },
    order: 20,
  },
  {
    key: 'payments',
    label: 'Payment Details',
    fieldPatterns: [/payment/i],
    filterDependencies: {
      includePayments: [
        'tas_application_payment_paid_amount_integer',
        'tas_application_payment_last_date_date',
      ],
      paymentYear: [
        'tas_application_payment_paid_amount_integer',
        'tas_application_payment_last_date_date',
      ],
    },
    order: 30,
    requiresIndividualApplications: true,
  },
  {
    key: 'invoices',
    label: 'Invoices',
    fieldPatterns: [/tas_app|tasApp|application/i], // Invoice date is a TAS download toggle
    filterDependencies: {},
    order: 40,
  },
  {
    key: 'courses',
    label: 'Courses',
    fieldPatterns: [/course/i],
    filterDependencies: {
      includeCourses: ['courses'],
      coursesBeginDate: [
        'courses',
        'tas_application_field_courses_begin_date_date',
        'tas_application_field_courses_end_date_date',
      ],
      coursesEndDate: [
        'courses',
        'tas_application_field_courses_begin_date_date',
        'tas_application_field_courses_end_date_date',
      ],
    },
    order: 50,
  },
  {
    key: 'employees',
    label: 'Employees',
    fieldPatterns: [/employee/i],
    filterDependencies: {
      terminatedEmployees: ['employee_termination_date'],
      excludeTermVerified: ['employee_term_verified_date'],
      employeeId: ['employee_name', 'employee_payroll_id_keyword'],
    },
    order: 60,
  },
  {
    key: 'approvers',
    label: 'Approvers',
    fieldPatterns: [/reviewer|approver/i],
    filterDependencies: {
      approverId: ['tas_application_possible_reviewers'],
    },
    order: 70,
  },
  {
    key: 'evidence',
    label: 'Evidence',
    fieldPatterns: [/evidence/i],
    filterDependencies: {},
    order: 80,
  },
  {
    key: 'eligibility',
    label: 'Eligibility',
    fieldPatterns: [/eligibility/i],
    filterDependencies: {},
    order: 90,
  },
  {
    key: 'participant',
    label: 'Participant',
    fieldPatterns: [/participant/i],
    filterDependencies: {},
    order: 100,
  },
  {
    key: 'withdrawal',
    label: 'Withdrawal',
    fieldPatterns: [/withdrawal/i],
    filterDependencies: {},
    order: 110,
  },
];

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Get all registered filter section configs.
 */
export function getAllFilterSections(): FilterSectionConfig[] {
  return [...FILTER_SECTIONS].sort((a, b) => a.order - b.order);
}

/**
 * Given a set of available field keys, return filter sections that have at least
 * one matching field. Respects admin-only and individual-application constraints.
 */
export function getVisibleFilterSections(
  availableFieldKeys: string[],
  options?: {
    isAdminContext?: boolean;
    showIndividualApplications?: boolean;
  }
): FilterSectionConfig[] {
  const { isAdminContext = false, showIndividualApplications = false } = options ?? {};

  return getAllFilterSections().filter((section) => {
    // Admin-only sections are hidden for non-admin users
    if (section.adminOnly && !isAdminContext) {
      return false;
    }

    // Sections that require individual applications mode
    if (section.requiresIndividualApplications && !showIndividualApplications) {
      return false;
    }

    // Sections with no field patterns are always visible (e.g., invoice date toggle)
    if (section.fieldPatterns.length === 0) {
      return true;
    }

    // Check if any field pattern matches available fields
    return section.fieldPatterns.some((pattern) =>
      availableFieldKeys.some((fieldKey) => pattern.test(fieldKey))
    );
  });
}

/**
 * Auto-derive the complete filter-to-column dependency map from all filter sections.
 * This replaces the manual `filterColumnDependencies` property.
 */
export function deriveFilterColumnDependencies(): Record<string, string[]> {
  const dependencies: Record<string, string[]> = {};

  for (const section of FILTER_SECTIONS) {
    for (const [filterKey, columns] of Object.entries(section.filterDependencies)) {
      dependencies[filterKey] = columns;
    }
  }

  return dependencies;
}

/**
 * Check if a specific filter section key has matching fields.
 */
export function isSectionAvailable(sectionKey: string, availableFieldKeys: string[]): boolean {
  const section = FILTER_SECTIONS.find((s) => s.key === sectionKey);
  if (!section) return false;

  // Sections with no field patterns are always visible
  if (section.fieldPatterns.length === 0) return true;

  return section.fieldPatterns.some((pattern) =>
    availableFieldKeys.some((fieldKey) => pattern.test(fieldKey))
  );
}
